import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, Surface } from "@/components/common";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ImageIcon, Trash2, UploadCloud, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { TeamPanel } from "@/components/TeamPanel";
import { useTheme } from "@/context/ThemeContext";

const PLAN_LABELS = { gratis: "Gratis", start: "Start", pro: "Pro", business: "Business" };

export default function Settings() {
  const { company, user, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t, locale } = useLocale();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [primary, setPrimary] = useState("#0F172A");
  const [accent, setAccent] = useState("#4F46E5");
  const [savingBrand, setSavingBrand] = useState(false);
  const logoRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  useEffect(() => {
    if (company) {
      setF({ name: company.name || "", website: company.website || "", industry: company.industry || "",
        description: company.description || "", audience: company.audience || "", linkedin: company.linkedin || "", tone: company.tone || "" });
      setPrimary(company.brand_primary || "#0F172A");
      setAccent(company.brand_accent || "#4F46E5");
    }
    api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, [company]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/company", f); await refresh(); toast.success("Profil opdateret"); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const saveBranding = async () => {
    setSavingBrand(true);
    try { await api.put("/company/branding", { brand_primary: primary, brand_accent: accent }); await refresh(); toast.success(t("Branding gemt")); }
    catch (err) { toast.error(apiError(err)); } finally { setSavingBrand(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); await refresh(); toast.success(t("Logo uploadet")); }
    catch (err) { toast.error(apiError(err)); }
  };

  const removeLogo = async () => {
    try { await api.delete("/company/logo"); await refresh(); toast.success(t("Logo fjernet")); }
    catch (err) { toast.error(apiError(err)); }
  };

  const [portalLoading, setPortalLoading] = useState(false);
  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err)); setPortalLoading(false); }
  };

  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: "", password: "" });
  const [savingEmail, setSavingEmail] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (user) { setName(user.name || ""); setEmailForm({ email: user.email || "", password: "" }); }
  }, [user]);

  const saveName = async () => {
    if (name.trim().length < 2) return toast.error(t("Navnet er for kort"));
    setSavingName(true);
    try { await api.put("/account/name", { name: name.trim() }); await refresh(); toast.success(t("Navn opdateret")); }
    catch (err) { toast.error(apiError(err)); } finally { setSavingName(false); }
  };

  const saveEmail = async () => {
    if (!emailForm.email.trim()) return toast.error(t("Indtast en e-mail"));
    if (!emailForm.password) return toast.error(t("Bekræft med din adgangskode"));
    setSavingEmail(true);
    try {
      const { data } = await api.put("/account/email", { email: emailForm.email.trim(), password: emailForm.password });
      if (data.token) localStorage.setItem("pt_token", data.token);
      await refresh();
      setEmailForm((s) => ({ ...s, password: "" }));
      toast.success(t("E-mail opdateret"));
    } catch (err) { toast.error(apiError(err)); } finally { setSavingEmail(false); }
  };

  const savePassword = async () => {
    if (pwForm.new_password.length < 8) return toast.error(t("Adgangskoden skal være mindst 8 tegn"));
    if (pwForm.new_password !== pwForm.confirm) return toast.error(t("De to adgangskoder er ikke ens"));
    setSavingPw(true);
    try {
      await api.put("/account/password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      toast.success(t("Adgangskode opdateret"));
    } catch (err) { toast.error(apiError(err)); } finally { setSavingPw(false); }
  };

  if (!f) return null;
  const pct = usage && usage.limit < 100000 ? Math.min(100, (usage.used / usage.limit) * 100) : null;
  const hasStripe = !!user?.stripe_customer_id;
  const hasSub = !!user?.stripe_subscription_id && user?.subscription_status && user.subscription_status !== "canceled";
  const periodEnd = user?.current_period_end ? new Date(user.current_period_end * 1000).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" }) : null;
  const renewText = periodEnd
    ? (user?.cancel_at_period_end ? `Abonnementet er opsagt og udløber ${periodEnd}.` : `Fornyes automatisk ${periodEnd}.`)
    : null;

  return (
    <div>
      <PageHeader overline="Indstillinger" title="Jeres virksomhedsprofil"
        subtitle="Denne profil bruges i al fremtidig kommunikation. Hold den opdateret." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Surface>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t("Virksomhedsnavn")}</Label><Input value={f.name} onChange={set("name")} data-testid="settings-name" /></div>
              <div className="space-y-2"><Label>{t("Website")}</Label><Input value={f.website} onChange={set("website")} data-testid="settings-website" /></div>
              <div className="space-y-2"><Label>{t("Branche")}</Label><Input value={f.industry} onChange={set("industry")} data-testid="settings-industry" /></div>
              <div className="space-y-2"><Label>{t("Tone of voice")}</Label><Input value={f.tone} onChange={set("tone")} data-testid="settings-tone" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>{t("Primær målgruppe")}</Label><Input value={f.audience} onChange={set("audience")} data-testid="settings-audience" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>{t("LinkedIn")}</Label><Input value={f.linkedin} onChange={set("linkedin")} data-testid="settings-linkedin" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>{t("Beskrivelse")}</Label><Textarea value={f.description} onChange={set("description")} rows={3} data-testid="settings-description" /></div>
            </div>
            <Button onClick={save} disabled={saving} className="mt-4" data-testid="settings-save-btn">{t("Gem ændringer")}</Button>
          </Surface>

          {company?.ai_profile && (
            <Surface testid="settings-ai-profile">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("AI-genereret profil")}</p>
              <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">{company.ai_profile}</p>
            </Surface>
          )}

          <Surface testid="settings-branding">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">{t("Branding")}</p>
            <p className="text-sm text-muted-foreground mb-5">{t("Logo og brandfarver bruges automatisk, når Aicom AI genererer PDF'er (fx lead magnets).")}</p>

            <div className="flex items-center gap-4 mb-6">
              <div className="h-16 w-16 rounded-lg border border-border bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                {company?.logo_data_url ? (
                  <img src={company.logo_data_url} alt="logo" className="max-h-14 max-w-14 object-contain" data-testid="settings-logo-preview" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex gap-2">
                <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="settings-logo-input"
                  onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <Button variant="outline" size="sm" className="gap-2" onClick={() => logoRef.current?.click()} data-testid="settings-logo-upload">
                  <UploadCloud className="h-4 w-4" /> {t("Upload logo")}
                </Button>
                {company?.logo_data_url && (
                  <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground" onClick={removeLogo} data-testid="settings-logo-remove">
                    <Trash2 className="h-4 w-4" /> {t("Fjern")}
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label>{t("Primær farve (forside)")}</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} data-testid="settings-color-primary"
                    className="h-9 w-12 rounded border border-border bg-transparent cursor-pointer" />
                  <Input value={primary} onChange={(e) => setPrimary(e.target.value)} className="font-mono text-xs" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("Accentfarve")}</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} data-testid="settings-color-accent"
                    className="h-9 w-12 rounded border border-border bg-transparent cursor-pointer" />
                  <Input value={accent} onChange={(e) => setAccent(e.target.value)} className="font-mono text-xs" />
                </div>
              </div>
            </div>
            <Button onClick={saveBranding} disabled={savingBrand} data-testid="settings-branding-save">{t("Gem branding")}</Button>
          </Surface>
        </div>

        <div className="space-y-4">
          <Surface testid="settings-account">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">{t("Konto")}</p>

            <div className="space-y-2">
              <Label>{t("Navn")}</Label>
              <div className="flex gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name-input" />
                <Button variant="outline" onClick={saveName} disabled={savingName} data-testid="account-name-save">{t("Gem")}</Button>
              </div>
            </div>

            <div className="my-5 h-px bg-border" />

            <p className="text-sm font-medium mb-2">{t("Skift e-mail")}</p>
            <div className="space-y-2">
              <Input type="email" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} placeholder={t("Ny e-mail")} data-testid="account-email-input" />
              <Input type="password" value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} placeholder={t("Nuværende adgangskode")} data-testid="account-email-password" />
              <Button variant="outline" className="w-full" onClick={saveEmail} disabled={savingEmail} data-testid="account-email-save">{t("Opdatér e-mail")}</Button>
            </div>

            <div className="my-5 h-px bg-border" />

            <p className="text-sm font-medium mb-2">{t("Skift adgangskode")}</p>
            <div className="space-y-2">
              <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} placeholder={t("Nuværende adgangskode")} data-testid="account-current-password" />
              <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} placeholder={t("Ny adgangskode (mindst 8 tegn)")} data-testid="account-new-password" />
              <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} placeholder={t("Gentag ny adgangskode")} data-testid="account-confirm-password" />
              <Button variant="outline" className="w-full" onClick={savePassword} disabled={savingPw} data-testid="account-password-save">{t("Opdatér adgangskode")}</Button>
            </div>
          </Surface>
          <Surface testid="settings-appearance">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">{t("Udseende")}</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setTheme("dark")} data-testid="theme-dark-btn"
                className={`flex items-center justify-center gap-2 h-11 rounded-xl border text-sm font-medium transition-colors ${theme === "dark" ? "border-foreground bg-secondary text-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <Moon className="h-4 w-4" /> Mørkt
              </button>
              <button onClick={() => setTheme("light")} data-testid="theme-light-btn"
                className={`flex items-center justify-center gap-2 h-11 rounded-xl border text-sm font-medium transition-colors ${theme === "light" ? "border-foreground bg-secondary text-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <Sun className="h-4 w-4" /> {t("Lyst")}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">{t("Vælg, hvordan appen skal se ud. Dit valg gemmes på denne enhed.")}</p>
          </Surface>
          <Surface testid="settings-plan">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">{t("Abonnement")}</p>
            <p className="font-display text-2xl font-medium">{t(PLAN_LABELS[user?.plan] || "Gratis")}</p>
            {usage && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-2">{pct !== null ? t("{used} kreditter brugt af {limit}", { used: usage.used, limit: usage.limit }) : t("{used} kreditter brugt · ubegrænset", { used: usage.used })}</p>
                {pct !== null && <Progress value={pct} className="h-1.5" />}
              </div>
            )}
            {hasSub && renewText && (
              <p className="text-xs text-muted-foreground mt-3" data-testid="settings-renew-info">{renewText}</p>
            )}
            {user?.is_owner ? (
              hasStripe ? (
                <div className="space-y-2 mt-4">
                  <Button variant="outline" className="w-full" onClick={openPortal} disabled={portalLoading} data-testid="settings-portal-btn">
                    {portalLoading ? t("Åbner...") : t("Administrér abonnement")}
                  </Button>
                  <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => (window.location.href = "/priser")} data-testid="settings-changeplan-btn">{t("Skift plan")}</Button>
                </div>
              ) : (
                <Button variant="outline" className="w-full mt-4" onClick={() => (window.location.href = "/priser")} data-testid="settings-upgrade-btn">{t("Se planer")}</Button>
              )
            ) : (
              <p className="text-xs text-muted-foreground mt-4">{t("Abonnementet styres af virksomhedens ejer.")}</p>
            )}
          </Surface>
          <TeamPanel />
        </div>
      </div>
    </div>
  );
}
